import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Plus, Calendar, Clock, Settings, Trash2, Copy, ExternalLink, BarChart3 } from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventTypes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eventTypes, setEventTypes] = useState([]);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleCalendarInfo, setGoogleCalendarInfo] = useState(null);
  const [showConnectPrompt, setShowConnectPrompt] = useState(false);

  useEffect(() => {
    loadEventTypes();
    checkGoogleCalendarStatus();
  }, []);

  const loadEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`, {
        headers: getAuthHeaders()
      });
      setEventTypes(response.data.eventTypes || []);
    } catch (error) {
      toast.error('Failed to load event types');
    } finally {
      setLoading(false);
    }
  };

  const copyBookingLink = (slug) => {
    const user = JSON.parse(localStorage.getItem('user'));
    const link = `${window.location.origin}/book/${user.id}/${slug}`;
    navigator.clipboard.writeText(link);
    toast.success('Booking link copied!');
  };

  const deleteEventType = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event type?')) return;
    
    try {
      await axios.delete(`${API}/event-types/${id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Event type deleted');
      loadEventTypes();
    } catch (error) {
      toast.error('Failed to delete event type');
    }
  };

  const checkGoogleCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/status`, {
        headers: getAuthHeaders()
      });
      setGoogleConnected(response.data.connected);
      if (response.data.connected) {
        setGoogleCalendarInfo(response.data);
      } else {
        // Show prompt if not connected and has event types
        const hasSeenPrompt = localStorage.getItem('google_calendar_prompt_seen');
        if (!hasSeenPrompt && eventTypes.length > 0) {
          setShowConnectPrompt(true);
        }
      }
    } catch (error) {
      console.error('Failed to check Google Calendar status:', error);
    }
  };

  const dismissPrompt = () => {
    setShowConnectPrompt(false);
    localStorage.setItem('google_calendar_prompt_seen', 'true');
  };

  const connectAndDismiss = async () => {
    setShowConnectPrompt(false);
    await connectGoogleCalendar();
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/calendar/google/connect`);
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error('Failed to connect Google Calendar');
    }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar? You can reconnect anytime.')) return;
    
    try {
      await axios.delete(`${API}/calendar/google/disconnect`, {
        headers: getAuthHeaders()
      });
      toast.success('Google Calendar disconnected');
      setGoogleConnected(false);
      setGoogleCalendarInfo(null);
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-slate-600">Loading event types...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Event Types
            </h1>
            <p className="text-slate-600">Create and manage your scheduling event types</p>
          </div>
          <Button
            onClick={() => navigate('/event-types/new')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Event Type
          </Button>
        </div>

        {/* Google Calendar Integration Card */}
        <Card className="p-6 mb-6 border-2 border-blue-100">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Google Calendar Sync
                </h3>
                {googleConnected ? (
                  <div>
                    <p className="text-sm text-green-600 font-medium mb-1">
                      ✓ Connected to {googleCalendarInfo?.calendar_name}
                    </p>
                    <p className="text-sm text-slate-600">
                      Your availability automatically syncs with Google Calendar to prevent double bookings
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-slate-600">
                    Connect your Google Calendar to automatically block busy times and prevent double bookings
                  </p>
                )}
              </div>
            </div>
            <div>
              {googleConnected ? (
                <Button
                  variant="outline"
                  onClick={disconnectGoogleCalendar}
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  onClick={connectGoogleCalendar}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Connect Google Calendar
                </Button>
              )}
            </div>
          </div>
        </Card>

        {eventTypes.length === 0 ? (
          <Card className="p-12 text-center">
            <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No event types yet</h3>
            <p className="text-slate-500 mb-6">Create your first event type to start accepting bookings</p>
            <Button
              onClick={() => navigate('/event-types/new')}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Event Type
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventTypes.map((eventType) => (
              <Card key={eventType.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: eventType.color }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/event-types/${eventType.id}/analytics`)}
                      className="text-slate-400 hover:text-blue-600"
                      title="Analytics"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => copyBookingLink(eventType.slug)}
                      className="text-slate-400 hover:text-blue-600"
                      title="Copy booking link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/event-types/${eventType.id}/edit`)}
                      className="text-slate-400 hover:text-slate-600"
                      title="Edit"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteEventType(eventType.id)}
                      className="text-slate-400 hover:text-red-600"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-slate-900 mb-2">{eventType.name}</h3>
                {eventType.description && (
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{eventType.description}</p>
                )}

                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{eventType.duration} minutes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{eventType.bookingsCount || 0} bookings</span>
                  </div>
                </div>

                {/* Booking Link Display - Fixed to prevent overflow */}
                <div className="mt-3 p-2 bg-slate-50 rounded border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Booking Link:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-blue-600 break-all flex-1">
                      {`${window.location.origin}/book/${eventType.userId}/${eventType.slug}`}
                    </code>
                    <button
                      onClick={() => copyBookingLink(eventType.slug)}
                      className="flex-shrink-0 p-1 text-slate-400 hover:text-blue-600"
                      title="Copy link"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded ${
                    eventType.isActive 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {eventType.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <a
                    href={`/book/${eventType.userId}/${eventType.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm"
                  >
                    View page
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Google Calendar Connection Prompt Modal */}
      {showConnectPrompt && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full p-8 bg-white shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Connect Google Calendar?
              </h2>
              <p className="text-slate-600">
                Sync your Google Calendar with DigiCalendar to prevent double bookings and keep your schedule organized.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">Benefits:</h3>
              <ul className="text-sm text-blue-800 space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>Automatically block times when you have Google Calendar events</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>Prevent double bookings across both calendars</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>Real-time availability sync</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-600">✓</span>
                  <span>Works in the background - set it and forget it!</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={connectAndDismiss}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Calendar className="w-4 h-4 mr-2" />
                Connect Now
              </Button>
              <Button
                variant="outline"
                onClick={dismissPrompt}
                className="flex-1"
              >
                Maybe Later
              </Button>
            </div>

            <p className="text-xs text-slate-500 text-center mt-4">
              You can always connect later from the settings
            </p>
          </Card>
        </div>
      )}
    </div>
  );
};

export default EventTypes;